import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { IdentificationTypeModal } from '../components/IdentificationTypeModal';
import { identificationApi } from '../identificationApi';
import type { IdentificationType } from '../identificationTypes';

/**
 * The identification catalog — what the personnel form's Identification step is built from.
 *
 * The anchor opens the **profile**, not the edit form: every index page in this product is
 * partnered with one, and here the profile is where the count of records holding a number of
 * this kind is worth reading before deciding anything about the row.
 */
export function IdentificationTypesIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<IdentificationType | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    type: IdentificationType;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchTypes = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      identificationApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (type: IdentificationType): MenuItem[] => [
      {
        key: `edit-${type.id}`,
        label: 'Edit',
        permission: 'identification-types.update',
        onClick: () => setEditing(type),
      },
      {
        key: `restore-${type.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'identification-types.delete',
        disabledReason: type.isActive ? 'This type is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', type }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await identificationApi.deactivate(confirming.type.id);
      else await identificationApi.activate(confirming.type.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<IdentificationType>
        module="identification-types"
        // Reached through Personnel → Settings, so Settings links: that crumb is the way back.
        breadcrumb={[
          'Human Resources',
          'Personnel',
          { label: 'Settings', to: '/personnel/settings' },
          'Identification',
        ]}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['IdentificationType']}
        rowKey={(type) => type.id}
        fetch={fetchTypes}
        reloadKey={reloadKey}
        searchPlaceholder="Search identification types"
        emptyMessage="No identification types yet. Add the numbers this employer collects."
        onCreate={() => setCreating(true)}
        createPermission="identification-types.create"
        // The anchor opens the profile; Edit is in the row menu. The read key, not the write
        // one — opening a record is not changing it.
        onRowOpen={(type) => navigate(`/personnel/settings/identification/${type.id}`)}
        openPermission="identification-types.read"
        rowActions={rowActions}
        onDelete={(type) => setConfirming({ kind: 'deactivate', type })}
        deletePermission="identification-types.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(type) => (type.isActive ? null : 'This type is already deleted.')}
        onClearData={() => identificationApi.clear()}
        clearDataPermission="identification-types.clear-data"
        otherInfo={(type) => type}
      />

      {creating || editing !== null ? (
        <IdentificationTypeModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={
            confirming.kind === 'deactivate'
              ? 'Delete identification type'
              : 'Restore identification type'
          }
          danger={confirming.kind === 'deactivate'}
          // Both safeguards on the destructive half only. Restoring needs no ceremony.
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`identification-type-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.type.name}" stops being asked for on the personnel form.`
                  : `"${confirming.type.name}" is asked for again on the personnel form.`}
              </p>

              {confirming.kind === 'deactivate' && confirming.type.recordCount > 0 ? (
                <p>
                  {confirming.type.recordCount} employee
                  {confirming.type.recordCount === 1 ? '' : 's'} already hold
                  {confirming.type.recordCount === 1 ? 's' : ''} a number of this kind. Those
                  numbers stay on their records and still name the type — removing a field does
                  not erase what was collected.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted type can be restored from this list.</p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<IdentificationType>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 260,
    flex: true,
    sortable: true,
    render: (type) => <TextCell value={type.name} />,
  },
  {
    key: 'code',
    header: 'Code',
    width: 110,
    sortable: true,
    summary: true,
    render: (type) => <TextCell value={type.code} mono />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 320,
    summary: true,
    render: (type) => <TextCell value={type.description} />,
  },
  {
    // The stored sort value, under a heading that says so: a POSITION is only correct with the
    // whole ladder in hand, and a paged table never has it.
    key: 'sortOrder',
    header: 'Order',
    width: 90,
    align: 'right',
    sortable: true,
    render: (type) => <TextCell value={String(type.sortOrder)} mono numeric />,
  },
  {
    key: 'recordCount',
    header: 'Records',
    width: 100,
    align: 'right',
    summary: true,
    // The count is of personnel records, which is another module's data — the name and code
    // beside it are this module's own and stay ungated.
    permission: 'personnel.read',
    render: (type) => <TextCell value={String(type.recordCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (type) =>
      type.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
